import styles from './styles.module.css';
import React, { useMemo } from 'react';
import { useTable, useGlobalFilter, useSortBy, usePagination } from 'react-table';
import { CaretIcon, SearchIcon } from 'src/icons';
import { formatDate } from 'src/utils/common';

const TableSearchAll = ({ globalFilter, setGlobalFilter }: any) => {
	const [value, setValue] = React.useState(globalFilter);
	const onChange = (value: string) => {
		setGlobalFilter(value || undefined);
	};

	return (
		<div className={styles.tableSearchContainer}>
			<span className={styles.tableSearchIconLeft}>
				<SearchIcon scale={0.9} />
			</span>
			<input
				className={styles.tableSearchInput}
				value={value || ''}
				onChange={(e) => {
					setValue(e.target.value);
					onChange(e.target.value);
				}}
				placeholder="Search Table"
				alt="Search Table"
			/>
		</div>
	);
};

interface TableProps {
	useTableResult: any;
}

const Table = (props: TableProps) => {
	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		prepareRow,
		page,
		canPreviousPage,
		canNextPage,
		pageOptions,
		pageCount,
		gotoPage,
		nextPage,
		previousPage,
		setPageSize,
		state: { pageIndex, pageSize, globalFilter },
		preGlobalFilteredRows,
		setGlobalFilter,
	} = props.useTableResult;

	const pageNr = pageIndex + 1;

	const validBetween = (minVal: number, maxVal: number, val: number) => {
		if (maxVal < minVal) return minVal;
		if (val >= maxVal) return maxVal;
		if (val <= minVal) return minVal;
		return val;
	};

	const pageNrLess1Btn = validBetween(2, pageCount - 3, pageNr - 1);
	const pageNrBtn = validBetween(3, pageCount - 2, pageNr);
	const pageNrPlus1Btn = validBetween(4, pageCount - 1, pageNr + 1);

	return (
		<div>
			<TableSearchAll globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} />
			<div className={styles.tableContainer}>
				<table className={styles.table} {...getTableProps}>
					<thead>
						{headerGroups.map((headerGroup: any) => (
							<tr {...headerGroup.getHeaderGroupProps()}>
								{headerGroup.headers.map((column: any) => (
									<th
										className={column.isSorted ? styles.tableSortedTh : undefined}
										{...column.getHeaderProps(column.getSortByToggleProps())}
									>
										{column.render('Header')}
										<span>
											{column.isSorted ? (
												column.isSortedDesc ? (
													<CaretIcon direction="up" />
												) : (
													<CaretIcon direction="down" />
												)
											) : (
												''
											)}
										</span>
									</th>
								))}
							</tr>
						))}
					</thead>
					<tbody {...getTableBodyProps()}>
						{page.map((row: any, i: number) => {
							prepareRow(row);
							return (
								<tr {...row.getRowProps()}>
									{row.cells.map((cell: any) => {
										return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>;
									})}
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
			<div className={styles.tableButtonsWrapper}>
				<div className={styles.tableButtonsContainer}>
					<button
						type="button"
						onClick={previousPage}
						disabled={!canPreviousPage}
						className={styles.tableButtonsFirst}
					>
						&lt;
					</button>

					<button
						type="button"
						onClick={() => gotoPage(0)}
						disabled={!canPreviousPage}
						className={pageNr === 1 ? styles.tableButtonPageActive : styles.tableButtonPage}
					>
						1
					</button>
					{pageCount >= 2 && (
						<button
							type="button"
							onClick={() => gotoPage(pageNrLess1Btn - 1)}
							disabled={pageCount < 2}
							className={
								pageNr === pageNrLess1Btn ? styles.tableButtonPageActive : styles.tableButtonPage
							}
						>
							{pageNrLess1Btn}
						</button>
					)}
					{pageCount >= 3 && (
						<button
							type="button"
							onClick={() => gotoPage(pageNrBtn - 1)}
							disabled={pageCount < 3}
							className={
								pageNr === pageNrBtn ? styles.tableButtonPageActive : styles.tableButtonPage
							}
						>
							{pageNrBtn}
						</button>
					)}
					{pageCount >= 4 && (
						<button
							type="button"
							onClick={() => gotoPage(pageNrPlus1Btn - 1)}
							disabled={Boolean(pageCount < 4)}
							className={
								pageNr === pageNrPlus1Btn ? styles.tableButtonPageActive : styles.tableButtonPage
							}
						>
							{pageNrPlus1Btn}
						</button>
					)}
					{pageCount >= 5 && (
						<button
							type="button"
							onClick={() => gotoPage(pageCount - 1)}
							disabled={!canNextPage}
							className={
								pageNr === pageCount ? styles.tableButtonPageActive : styles.tableButtonPage
							}
						>
							{pageCount}
						</button>
					)}

					<button
						type="button"
						onClick={nextPage}
						disabled={!canNextPage}
						className={styles.tableButtonLast}
					>
						&gt;
					</button>
				</div>
			</div>
		</div>
	);
};

export type aavData = {
	id?: string;
	accreditationNumber?: string;
	surname?: string;
	firstName?: string;
	NSW?: boolean | string;
	ACT?: boolean | string;
	VIC?: boolean | string;
	TAS?: boolean | string;
	SA?: boolean | string;
	WA?: boolean | string;
	NT?: boolean | string;
	QLD?: boolean | string;
	exportShip?: boolean | string;
	suburbOrTown?: string;
	state?: string;
	landline?: string;
	mobile?: string;
	email?: string;
	notes?: string;
};

interface AavTableProps {
	data: aavData[];
}

export const AavTable = (props: AavTableProps) => {
	const columns = useMemo(
		() => [
			{
				Header: 'SURNAME',
				accessor: 'surname',
			},
			{
				Header: 'FIRST NAME',
				accessor: 'firstName',
			},
			{
				Header: 'EMAIL',
				accessor: 'email',
			},
			{
				Header: 'LANDLINE',
				accessor: 'landline',
			},
			{
				Header: 'MOBILE',
				accessor: 'mobile',
			},
			{
				Header: 'ACCREDITATION NUMBER',
				accessor: 'accreditationNumber',
			},
			{
				Header: 'EXPORT SHIP',
				accessor: 'exportShip',
			},
			{
				Header: 'NSW',
				accessor: 'NSW',
			},
			{
				Header: 'ACT',
				accessor: 'ACT',
			},
			{
				Header: 'VIC',
				accessor: 'VIC',
			},
			{
				Header: 'TAS',
				accessor: 'TAS',
			},
			{
				Header: 'SA',
				accessor: 'SA',
			},
			{
				Header: 'WA',
				accessor: 'WA',
			},
			{
				Header: 'NT',
				accessor: 'NT',
			},
			{
				Header: 'QLD',
				accessor: 'QLD',
			},
			{
				Header: 'SUBURB / TOWN',
				accessor: 'suburbOrTown',
			},
			{
				Header: 'STATE',
				accessor: 'state',
			},
			{
				Header: 'NOTES',
				accessor: 'notes',
			},
		],
		[]
	);
	const tableData = useMemo(() => {
		return props.data.map((aav) => {
			let result = { ...aav };
			for (const [key, value] of Object.entries(result)) {
				if (value === true || value === false) {
					(result as any)[key] = value ? 'Yes' : 'No';
				}
			}
			return result;
		});
	}, props.data);

	const useTableResult = useTable<aavData>(
		{
			columns,
			data: tableData,
			initialState: {
				sortBy: [{ id: 'surname' }],
			},
		},
		useGlobalFilter,
		useSortBy,
		usePagination
	);

	return <Table useTableResult={useTableResult} />;
};

export type stockPersonData = {
	id?: string;
	accreditationNumber?: string;
	surname?: string;
	firstName?: string;
	telephone?: string;
	email?: string;
	state?: string;
	notes?: string;
};

interface stockPersonTableProps {
	data: stockPersonData[];
}

export const StockPersonTable = (props: stockPersonTableProps) => {
	const columns = useMemo(
		() => [
			{
				Header: 'ACCREDITATION NUMBER',
				accessor: 'accreditationNumber',
			},
			{
				Header: 'SURNAME',
				accessor: 'surname',
			},
			{
				Header: 'FIRST NAME',
				accessor: 'firstName',
			},
			{
				Header: 'TELEPHONE',
				accessor: 'telephone',
			},
			{
				Header: 'EMAIL',
				accessor: 'email',
			},
			{
				Header: 'STATE',
				accessor: 'state',
			},
			{
				Header: 'NOTES',
				accessor: 'notes',
			},
		],
		[]
	);
	const tableData = useMemo(() => {
		return props.data;
	}, props.data);

	const useTableResult = useTable<stockPersonData>(
		{
			columns,
			data: tableData,
			initialState: {
				sortBy: [{ id: 'surname' }],
			},
		},
		useGlobalFilter,
		useSortBy,
		usePagination
	);

	return <Table useTableResult={useTableResult} />;
};
